import { useMemo } from "react";
import "./MindMappingReportASeventhSection.css";

/* ── Goal ID → name ───────────────────────────────────────────── */
const GOAL_LOOKUP = {
  "1":  "Learning & Knowledge",
  "2":  "Technology & Digital Behavior",
  "3":  "Work & Career Habits",
  "4":  "Money & Finance",
  "5":  "Health & Wellness",
  "6":  "Lifestyle & Daily Routines",
  "7":  "Social & Community Behavior",
  "8":  "Entertainment & Creativity",
  "9":  "Shopping & Consumption",
  "10": "Problem-Solving & Frustrations",
  "11": "Values & Beliefs",
  "12": "Curiosity & Exploration",
};

/* ── Goal → domain + strategic focus builder ─────────────────── */
const GOAL_DETAILS = {
  "Learning & Knowledge": {
    focusArea: "EdTech & Knowledge Platforms",
    signal: (sector) =>
      `Build learning solutions that close skill gaps within the ${sector} industry — online courses, simulation tools, or certification platforms have strong demand and recurring revenue potential.`,
  },
  "Technology & Digital Behavior": {
    focusArea: "Digital Transformation & SaaS",
    signal: (sector) =>
      `Digitize workflows or customer interactions in the ${sector} space — SaaS tools, mobile-first platforms, and API-driven products align strongly with this goal and attract tech-savvy investors.`,
  },
  "Work & Career Habits": {
    focusArea: "HR Tech & Productivity Tools",
    signal: (sector) =>
      `Build tools that improve hiring, performance, or daily workflows for ${sector} professionals — HR Tech and productivity SaaS are high-growth categories with proven enterprise demand.`,
  },
  "Money & Finance": {
    focusArea: "Fintech & Financial Inclusion",
    signal: (sector) =>
      `Address financial access gaps or monetization challenges in the ${sector} sector — payment infra, lending tools, or insurance products in this space can attract both VC and bank partnerships.`,
  },
  "Health & Wellness": {
    focusArea: "HealthTech & Wellness Platforms",
    signal: (sector) =>
      `Integrate health outcomes or wellness programs into the ${sector} value chain — HealthTech has a large underserved market in India with strong government push (NHA, ABDM) and investor interest.`,
  },
  "Lifestyle & Daily Routines": {
    focusArea: "Consumer Apps & D2C",
    signal: (sector) =>
      `Build habit-forming consumer products that improve daily life within or adjacent to the ${sector} market — D2C brands and subscription apps in this space build strong LTV and repeat purchases.`,
  },
  "Social & Community Behavior": {
    focusArea: "Community Platforms & Social Tech",
    signal: (sector) =>
      `Create community-driven tools or platforms connecting ${sector} stakeholders — network-effect businesses here compound quickly and can dominate through viral growth without heavy ad spend.`,
  },
  "Entertainment & Creativity": {
    focusArea: "Creator Economy & Media Tech",
    signal: (sector) =>
      `Build content, creator tools, or entertainment experiences layered onto the ${sector} vertical — India's creator economy is expanding rapidly and monetization models are maturing fast.`,
  },
  "Shopping & Consumption": {
    focusArea: "E-Commerce & Retail Tech",
    signal: (sector) =>
      `Innovate within the ${sector} purchase journey — discovery tools, comparison platforms, or last-mile delivery solutions in this space have a proven path to revenue and scale.`,
  },
  "Problem-Solving & Frustrations": {
    focusArea: "Pain-Point SaaS & Vertical Tools",
    signal: (sector) =>
      `Your orientation toward solving specific frustrations is perfectly suited to building vertical SaaS or workflow automation for the ${sector} sector — the best B2B products fix painful problems, not nice-to-haves.`,
  },
  "Values & Beliefs": {
    focusArea: "Impact Ventures & Conscious Brands",
    signal: (sector) =>
      `Build a mission-driven brand or social enterprise within the ${sector} ecosystem — impact investors (Aavishkaar, Acumen) and conscious consumers increasingly reward purpose-built ventures.`,
  },
  "Curiosity & Exploration": {
    focusArea: "Deep Tech & Research Ventures",
    signal: (sector) =>
      `Your curiosity-driven mindset is well-matched to deep tech or R&D-heavy startups in the ${sector} space — these ventures attract DST grants, IIT incubators, and patient capital from family offices.`,
  },
};

const getSno = (index) => `7 ${String.fromCharCode(65 + index)}`;

const MindMappingReportASeventhSection = ({ res, toggle }) => {
  const sector = res?.sector ?? "your chosen sector";

  const userGoals = useMemo(() => {
    if (!res?.goals) return [];
    return res.goals.map((id) => GOAL_LOOKUP[id.toString()] ?? `Goal ${id}`);
  }, [res?.goals]);

  return (
    <div className="mind-mapping-report-a-seventh-container">
      <div className={toggle ? "table-adjust" : "mind-mapping-report-a-seventh-final-table"}>
        <span className="mind-mapping-report-a-seventh-corner mind-mapping-report-a-seventh-corner-top-left" />
        <span className="mind-mapping-report-a-seventh-corner mind-mapping-report-a-seventh-corner-top-right" />
        <span className="mind-mapping-report-a-seventh-corner mind-mapping-report-a-seventh-corner-bottom-left" />
        <span className="mind-mapping-report-a-seventh-corner mind-mapping-report-a-seventh-corner-bottom-right" />

        <div className="mind-mapping-report-a-seventh-module-title">
          <span className="module-prefix">Module 7:</span> Goal-to-Sector Alignment &amp; Startup Opportunity Map
        </div>

        <div className="mind-mapping-report-a-seventh-table-scroll-wrapper">
          <table className="mind-mapping-report-a-seventh-inner-table">
            <thead>
              <tr>
                <th className="mind-mapping-report-a-seventh-col-sno">S.NO</th>
                <th>Startup Domain</th>
                <th>Selected Core Goal</th>
                <th>Sector-Aligned Strategic Signal</th>
              </tr>
            </thead>
            <tbody>
              {userGoals.length > 0 ? (
                userGoals.map((goal, index) => {
                  const details = GOAL_DETAILS[goal];
                  return (
                    <tr key={index}>
                      <td className="mind-mapping-report-a-seventh-col-sno">{getSno(index)}</td>
                      <td>{details?.focusArea ?? "Cross-Sector Innovation"}</td>
                      <td className="mind-mapping-report-a-seventh-user-val">{goal}</td>
                      <td className="mind-mapping-report-a-seventh-highlight">
                        {details?.signal(sector) ??
                          `This goal aligns with innovation opportunities in the ${sector} space — explore niche problems that intersect your goal with the sector's core pain points.`}
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td className="mind-mapping-report-a-seventh-col-sno">7 A</td>
                  <td colSpan="3" style={{ textAlign: "center", padding: "20px" }}>
                    No specific goals selected.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default MindMappingReportASeventhSection;
